import type {CSSProperties} from 'react';

/* How the hit sheet treats the `::before` of a look, as tokens of the `data-rv-hit` attribute:
   - `own`: another sheet owns the pseudo element (a ring, a badge), so the hit sheet draws nothing there;
   - `none`: the look draws no transparent target, so the hit sheet clears the content (`content: none`).
   A look with no token gets the plain 44px target. */
export type WedgeHitToken = 'own' | 'none';

/* One family of `wedge-settings-action` looks. Every family lives in its own wedge*Button.ts file; the registry lists them. */
export interface WedgeLookFamily<L extends string = string> {
  /* True for the looks of this family. The registry tries the families in order and the first match styles the look. */
  readonly is: (look: string) => look is L;
  /* Style of a look, when the family ignores `disabled`. */
  readonly style?: (look: L) => CSSProperties;
  /* Style of a look, when the family reads `disabled`. */
  readonly styleDisabled?: (look: L, disabled: boolean) => CSSProperties;
  /* Looks that draw no transparent target, no anchor and no stacking context. `false` means every look of the family. A look
     draws a target only when every family agrees, so a family may name a look that another family styles. */
  readonly drawsHit?: false | ((look: string) => boolean);
  /* Explicit hit sheet tokens for a look, for the few looks that do not follow the default: no target draws `none`, a target draws
     nothing. An empty array keeps the plain target on a look that has no anchor. Undefined leaves the look to the default. */
  readonly before?: (look: string) => readonly WedgeHitToken[] | undefined;
  /* Extra hit sheet rules of the family (a size exception). The registry joins them in order after the base rule. */
  readonly css?: string;
}

/* Identity helper that infers the look type of a family from `is`. */
export const family = <L extends string>(f: WedgeLookFamily<L>): WedgeLookFamily<L> => f;

/* The look union of a family list: the registry derives `WedgeSettingsLook` from it, so there is no hand-kept union. */
export type FamilyLook<F> = F extends WedgeLookFamily<infer L> ? L : never;
